import React, { useEffect, useState } from "react";
import axios from "axios";
import HiddenNSReport from "./HiddenNSReport";
import SurveyInviteWidget from "./SurveyInviteWidget";

// ── Band → colour map (high / mod_to_high / mod_to_low / low) ────────────────
const bandColor = (band) => {
  if (!band) return { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
  switch (band.toLowerCase()) {
    case "high":        return { bg: "#dcfce7", color: "#1f8f55", border: "#bbf7d0" }; // green
    case "mod_to_high": return { bg: "#dbeafe", color: "#1d4ed8", border: "#bfdbfe" }; // blue
    case "mod_to_low":  return { bg: "#fef3c7", color: "#d88a1f", border: "#fde68a" }; // amber
    case "low":         return { bg: "#fee2e2", color: "#b91c1c", border: "#fecaca" }; // red
    default:            return { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
  }
};

// ── Dominant-style → badge colour map ────────────────────────────────────────
const styleColor = (style) => {
  switch ((style || "").toLowerCase()) {
    case "collaborating":  return { bg: "#dcfce7", color: "#1f8f55", border: "#bbf7d0" }; // green
    case "compromising":   return { bg: "#dbeafe", color: "#1d4ed8", border: "#bfdbfe" }; // blue
    case "accommodating":  return { bg: "#ede9fe", color: "#6d28d9", border: "#ddd6fe" }; // purple
    case "competing":      return { bg: "#ffedd5", color: "#c2410c", border: "#fed7aa" }; // orange
    case "avoiding":       return { bg: "#f3f4f6", color: "#4b5563", border: "#d1d5db" }; // grey
    default:               return { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
  }
};

// ── Full style-name → two-letter code (matches HiddenNSReport's dominantStyles check) ──
const STYLE_NAME_TO_CODE = {
  competing:     "CO",
  avoiding:      "AV",
  collaborating: "CL",
  accommodating: "AC",
  compromising:  "CM",
};

// ── The five NS styles in display order ───────────────────────────────────────
const NS_STYLES = [
  { key: "collaborating", code: "CL", label: "Collaborating" },
  { key: "compromising",  code: "CM", label: "Compromising"  },
  { key: "accommodating", code: "AC", label: "Accommodating" },
  { key: "competing",     code: "CO", label: "Competing"     },
  { key: "avoiding",      code: "AV", label: "Avoiding"      },
];

// ── Style descriptions (tooltip) ─────────────────────────────────────────────
const STYLE_DESC = {
  collaborating: "Open and creative — seeks solutions satisfying both parties; high Assertiveness, high Cooperativeness.",
  compromising:  "Middle-ground seeker — aims for mutual partial satisfaction; moderate Assertiveness and Cooperativeness.",
  accommodating: "Relationship-focused — prioritises the other party's needs; low Assertiveness, high Cooperativeness.",
  competing:     "Results-oriented and assertive — focuses on own bottom line; high Assertiveness, low Cooperativeness.",
  avoiding:      "Passive — prefers to sidestep conflict; low Assertiveness, low Cooperativeness.",
};

export default function GlobalNSResponses({ surveyId }) {
  const [nsData,         setNsData]         = useState([]);
  const [loading,        setLoading]        = useState(true);

  // ── Filters ──
  const [filterName,       setFilterName]       = useState("");
  const [filterEmail,      setFilterEmail]      = useState("");
  const [filterCompany,    setFilterCompany]    = useState("");
  const [filterDept,       setFilterDept]       = useState("");
  const [filterStyle,      setFilterStyle]      = useState("");
  const [filterStartDate,  setFilterStartDate]  = useState("");
  const [filterEndDate,    setFilterEndDate]    = useState("");

  // ── PDF state ──
  const [downloadData,    setDownloadData]    = useState(null);
  const [isGeneratingRow, setIsGeneratingRow] = useState(null);

  // ================= FETCH =================
  useEffect(() => {
    if (surveyId) fetchNSResults();
  }, [surveyId]);

  const fetchNSResults = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/response/calculate-score/negotiation/all`,
        { params: { survey_id: surveyId } }
      );
      setNsData(res.data.data || []);
    } catch (err) {
      console.error("❌ Failed to fetch Negotiation Style results", err);
    } finally {
      setLoading(false);
    }
  };

  // ================= FILTER =================
  // Collect all unique dominant styles across respondents
  const uniqueStyles = [
    ...new Set(
      nsData.flatMap(u => u.scores?.dominant_style || []).filter(Boolean)
    ),
  ].sort();

  const uniqueCompanies = [
    ...new Set(nsData.map((u) => (u?.company || "").trim()).filter(Boolean)),
  ].sort();

  const uniqueDepts = [
    ...new Set(nsData.map((u) => (u?.department || "").trim()).filter(Boolean)),
  ].sort();

  const filteredData = nsData.filter((user) => {
    const name           = (user?.name  || "").toLowerCase();
    const email          = (user?.email || "").toLowerCase();
    const company        = (user?.company || "").trim().toLowerCase();
    const dept           = (user?.department || "").trim().toLowerCase();
    const dominantStyles = (user?.scores?.dominant_style || []).map(s => s.toLowerCase());

    const matchName    = name.includes(filterName.toLowerCase());
    const matchEmail   = email.includes(filterEmail.toLowerCase());
    const matchCompany = filterCompany === "" || company === filterCompany.toLowerCase();
    const matchDept    = filterDept    === "" || dept    === filterDept.toLowerCase();
    const matchStyle   =
      filterStyle === "" || dominantStyles.includes(filterStyle.toLowerCase());

    let matchDate = true;
    if (filterStartDate || filterEndDate) {
      const submissionDate = new Date(user.submission_time);
      submissionDate.setHours(0, 0, 0, 0);
      if (filterStartDate) {
        const start = new Date(filterStartDate);
        start.setHours(0, 0, 0, 0);
        if (submissionDate < start) matchDate = false;
      }
      if (filterEndDate) {
        const end = new Date(filterEndDate);
        end.setHours(23, 59, 59, 999);
        if (submissionDate > end) matchDate = false;
      }
    }

    return matchName && matchEmail && matchCompany && matchDept && matchStyle && matchDate;
  });

  const clearFilters = () => {
    setFilterName("");
    setFilterEmail("");
    setFilterCompany("");
    setFilterDept("");
    setFilterStyle("");
    setFilterStartDate("");
    setFilterEndDate("");
  };

  // ── Summary counts (one tally per respondent's dominant style(s)) ──
  const styleCounts = nsData.reduce((acc, u) => {
    (u.scores?.dominant_style || []).forEach((s) => {
      const key = s.toLowerCase();
      acc[key] = (acc[key] || 0) + 1;
    });
    return acc;
  }, {});

  // ================= PDF DOWNLOAD =================
  const handleDownloadPDF = (user, rowIndex) => {
    setIsGeneratingRow(rowIndex);
    const sc     = user.scores || {};
    const styles = sc.styles   || {};

    // HiddenNSReport expects scores as a flat { CO: 14, AV: 19, CL: 16, … } map
    const flatScores = {};
    Object.entries(styles).forEach(([name, val]) => {
      const code = STYLE_NAME_TO_CODE[name.toLowerCase()];
      if (code) flatScores[code] = val?.score ?? null;
    });

    // HiddenNSReport.dominantStyles must be code strings e.g. ["AV"], not full names
    const dominantStyleCodes = (sc.dominant_style || []).map(
      (name) => STYLE_NAME_TO_CODE[name.toLowerCase()] || name.toUpperCase()
    );

    setDownloadData({
      userInfo: {
        name:  user?.name  || "Participant",
        email: user?.email || "",
        phone: user?.phone || "",
      },
      nsData:         sc,                  // full scores object — provides styles, assertiveness, cooperativeness
      scores:         flatScores,          // flat code map — used as fallback inside HiddenNSReport
      dominantStyles: dominantStyleCodes,  // e.g. ["AV"] or ["CL", "AC", "CM"]
      insightText:    "",
      submission_time: user.submission_time,
    });
  };

  const handleDownloadComplete = () => {
    setDownloadData(null);
    setIsGeneratingRow(null);
  };

  // ================= UI =================
  if (loading)
    return <div className="loading-state">Loading Negotiation Style data...</div>;

  return (
    <div style={{ marginTop: "20px" }}>
       <SurveyInviteWidget surveyId={surveyId} />

      {/* Hidden PDF renderer */}
      <HiddenNSReport
        participantData={downloadData}
        onComplete={handleDownloadComplete}
      />

      {/* ── Header ── */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: "15px",
      }}>
        <h3 style={{ margin: 0 }}>🤝 Negotiation Style Responses ({filteredData.length})</h3>
        <span style={{
          background: "#A81026", color: "white",
          padding: "5px 10px", borderRadius: "4px",
          fontSize: "12px", fontWeight: "bold",
        }}>
          Global Public View
        </span>
      </div>

      {/* ── Dominant-style summary pills ── */}
      {nsData.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "16px" }}>
          {Object.entries(styleCounts).map(([style, count]) => {
            const sc = styleColor(style);
            return (
              <span
                key={style}
                onClick={() => setFilterStyle(filterStyle === style ? "" : style)}
                title={`Click to filter by ${style}`}
                style={{
                  background:    sc.bg,
                  color:         sc.color,
                  border:        `1px solid ${sc.border}`,
                  padding:       "4px 12px",
                  borderRadius:  "999px",
                  fontSize:      "12px",
                  fontWeight:    "700",
                  cursor:        "pointer",
                  outline:       filterStyle === style ? `2px solid ${sc.color}` : "none",
                  outlineOffset: "2px",
                }}
              >
                {style.charAt(0).toUpperCase() + style.slice(1)}: {count}
              </span>
            );
          })}
          <span style={{ fontSize: "12px", color: "#888", alignSelf: "center", marginLeft: "4px" }}>
            ← click to filter
          </span>
        </div>
      )}

      {/* ── Filter Bar ── */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: "10px",
        background: "#f8f9fa", padding: "15px", borderRadius: "8px",
        border: "1px solid #ddd", marginBottom: "20px", alignItems: "center",
      }}>
        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Name</label>
          <input
            type="text"
            placeholder="Search name..."
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Email</label>
          <input
            type="text"
            placeholder="Search email..."
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Company</label>
          <select
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Companies</option>
            {uniqueCompanies.map(c => (
              <option key={c} value={c.toLowerCase()}>{c}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Department</label>
          <select
            value={filterDept}
            onChange={(e) => setFilterDept(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Departments</option>
            {uniqueDepts.map(d => (
              <option key={d} value={d.toLowerCase()}>{d}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Dominant Style</label>
          <select
            value={filterStyle}
            onChange={(e) => setFilterStyle(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Styles</option>
            {uniqueStyles.map(s => (
              <option key={s} value={s.toLowerCase()}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "130px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Start Date</label>
          <input
            type="date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "130px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>End Date</label>
          <input
            type="date"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ marginTop: "20px" }}>
          <button
            onClick={clearFilters}
            style={{
              padding: "8px 15px", background: "#A81026", color: "white",
              border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "bold",
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      {nsData.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>
          No Negotiation Style responses found.
        </div>
      ) : filteredData.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>
          No responses match your current filters.
        </div>
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: "8px", background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead>
              <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #ddd" }}>
                <th style={{ padding: "12px" }}>Date</th>
                <th style={{ padding: "12px" }}>Participant</th>
                <th style={{ padding: "12px" }}>Company / Dept</th>
                <th style={{ padding: "12px", minWidth: "260px" }}>Style Scores</th>
                <th style={{ padding: "12px", minWidth: "180px", background: "#fdf6e3" }}>
                  Assertiveness &amp; Cooperativeness
                </th>
                <th style={{ padding: "12px", minWidth: "120px", background: "#fdf6e3" }}>
                  🤝 Dominant Style
                </th>
                <th style={{ padding: "12px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredData.map((user, index) => {
                const sc           = user.scores || {};
                const styles       = sc.styles   || {};
                const dominantArr  = sc.dominant_style || [];
                const assertive    = sc.assertiveness  || {};
                const cooperative  = sc.cooperativeness || {};
                const isGenerating = isGeneratingRow === index;

                return (
                  <tr key={index} style={{ borderBottom: "1px solid #eee" }}>

                    {/* ── Date ── */}
                    <td style={{ padding: "12px", whiteSpace: "nowrap" }}>
                      {user.submission_time
                        ? <>
                            {new Date(user.submission_time).toLocaleDateString("en-US", {
                              month: "short", day: "numeric", year: "numeric",
                            })}<br />
                            <span style={{ fontSize: "12px", color: "#888" }}>
                              {new Date(user.submission_time).toLocaleTimeString("en-US", {
                                hour: "2-digit", minute: "2-digit",
                              })}
                            </span>
                          </>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>—</span>
                      }
                    </td>

                    {/* ── Participant ── */}
                    <td style={{ padding: "12px" }}>
                      <b>{user?.name || "Unknown"}</b><br />
                      <span style={{ fontSize: "13px", color: "#555" }}>
                        {user?.email}<br />
                        {user?.phone || ""}
                      </span>
                    </td>

                    {/* ── Company / Dept ── */}
                    <td style={{ padding: "12px" }}>
                      {user?.company
                        ? <><b>{user.company}</b><br /></>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>N/A</span>
                      }
                      {user?.department
                        ? <span style={{ fontSize: "13px", color: "#555" }}>{user.department}</span>
                        : null
                      }
                    </td>

                    {/* ── Style score pills ── */}
                    <td style={{ padding: "12px" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {NS_STYLES.map(({ key, code, label }) => {
                          const dim  = styles[key] || {};
                          const score = dim.score ?? null;
                          const band  = dim.band   ?? null;
                          const c     = bandColor(band);
                          return (
                            <span
                              key={key}
                              title={`${label}: ${score ?? "—"} pts · Band: ${dim.band_label ?? "—"}`}
                              style={{
                                background:   c.bg,
                                color:        c.color,
                                border:       `1px solid ${c.border}`,
                                padding:      "2px 8px",
                                borderRadius: "4px",
                                fontSize:     "12px",
                                fontWeight:   "700",
                                whiteSpace:   "nowrap",
                              }}
                            >
                              {code}: {score ?? "—"}
                              {band && (
                                <span style={{ fontWeight: "500", marginLeft: "4px", opacity: 0.8 }}>
                                  ({dim.band_label ?? band})
                                </span>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    </td>

                    {/* ── Assertiveness & Cooperativeness ── */}
                    <td style={{ padding: "12px", background: "#fffdf7" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        {/* Assertiveness */}
                        {(() => {
                          const c = bandColor(assertive.band);
                          return (
                            <span
                              title={`Formula: ${assertive.formula ?? ""} · Bargaining: ${assertive.bargaining_tendency ?? ""}`}
                              style={{
                                background:   c.bg,
                                color:        c.color,
                                border:       `1px solid ${c.border}`,
                                padding:      "2px 8px",
                                borderRadius: "4px",
                                fontSize:     "12px",
                                fontWeight:   "700",
                                whiteSpace:   "nowrap",
                              }}
                            >
                              Assert: {assertive.index ?? "—"}
                              {assertive.band_label && (
                                <span style={{ fontWeight: "500", marginLeft: "4px", opacity: 0.8 }}>
                                  ({assertive.band_label})
                                </span>
                              )}
                            </span>
                          );
                        })()}

                        {/* Cooperativeness */}
                        {(() => {
                          const c = bandColor(cooperative.band);
                          return (
                            <span
                              title={`Formula: ${cooperative.formula ?? ""} · Bargaining: ${cooperative.bargaining_tendency ?? ""}`}
                              style={{
                                background:   c.bg,
                                color:        c.color,
                                border:       `1px solid ${c.border}`,
                                padding:      "2px 8px",
                                borderRadius: "4px",
                                fontSize:     "12px",
                                fontWeight:   "700",
                                whiteSpace:   "nowrap",
                              }}
                            >
                              Coop: {cooperative.index ?? "—"}
                              {cooperative.band_label && (
                                <span style={{ fontWeight: "500", marginLeft: "4px", opacity: 0.8 }}>
                                  ({cooperative.band_label})
                                </span>
                              )}
                            </span>
                          );
                        })()}
                      </div>
                    </td>

                    {/* ── Dominant Style badge(s) ── */}
                    <td style={{ padding: "12px", background: "#fffdf7" }}>
                      {dominantArr.length > 0 ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                          {dominantArr.map((s) => {
                            const sc = styleColor(s);
                            return (
                              <span
                                key={s}
                                title={STYLE_DESC[s.toLowerCase()] || ""}
                                style={{
                                  background:   sc.bg,
                                  color:        sc.color,
                                  border:       `1px solid ${sc.border}`,
                                  padding:      "4px 10px",
                                  borderRadius: "999px",
                                  fontSize:     "12px",
                                  fontWeight:   "800",
                                  whiteSpace:   "nowrap",
                                }}
                              >
                                {s.charAt(0).toUpperCase() + s.slice(1)}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span style={{ color: "#aaa", fontStyle: "italic" }}>—</span>
                      )}
                    </td>

                    {/* ── Download ── */}
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <button
                        onClick={() => handleDownloadPDF(user, index)}
                        disabled={isGenerating}
                        style={{
                          background:   isGenerating ? "#95a5a6" : "#A81026",
                          color:        "white",
                          border:       "none",
                          padding:      "8px 12px",
                          borderRadius: "6px",
                          cursor:       isGenerating ? "not-allowed" : "pointer",
                          fontWeight:   "bold",
                          fontSize:     "12px",
                          display:      "flex",
                          alignItems:   "center",
                          gap:          "5px",
                          margin:       "0 auto",
                          transition:   "background 0.3s",
                          whiteSpace:   "nowrap",
                        }}
                      >
                        {isGenerating ? "⏳ Generating..." : "📄 Download"}
                      </button>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}